import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Camera, ChevronRight, MapPin, LogOut, Settings, Crown, 
  Gift, Users, Trophy, Heart, MessageCircle, Eye, 
  Shield, HelpCircle, FileText, Lock, Bell, CheckCircle,
  Star, Diamond, Pencil, Calendar, Trash2
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import { formatLocation } from "@/utils/recoveryTerminology";

const ProfilePage = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [matchStats, setMatchStats] = useState(null);
  const [profileCompletion, setProfileCompletion] = useState(null);

  const subscriptionTier = user?.subscription_tier || 'free';
  const isPlus = subscriptionTier === 'recovery_plus' || subscriptionTier === 'recovery_elite';
  const isElite = subscriptionTier === 'recovery_elite';

  const fetchStats = useCallback(async () => {
    try {
      const statsRes = await api.getMatchStats();
      setMatchStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchProfileCompletion = useCallback(async () => {
    try {
      const res = await api.get('/profile/completion');
      setProfileCompletion(res.data);
    } catch (error) {
      console.error('Failed to fetch profile completion:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchProfileCompletion();
    if (refreshUser) refreshUser();
  }, [fetchStats, fetchProfileCompletion, refreshUser]);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      navigate('/');
    }
  };

  const formatSobriety = (cleanDate) => {
    if (!cleanDate) return 'Not set';
    const start = new Date(cleanDate);
    const now = new Date();
    const days = Math.floor((now - start) / (1000 * 60 * 60 * 24));
    if (days < 30) return `${days} days`;
    if (days < 365) return `${Math.floor(days / 30)} months`;
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    return months > 0 ? `${years}y ${months}m` : `${years} years`;
  };

  const getTierInfo = () => {
    if (isElite) {
      return { name: 'Recovery Elite', color: 'text-[#1C3A7A]', bgColor: 'bg-[#FAB710]/30', icon: Diamond };
    }
    if (isPlus) {
      return { name: 'Recovery+', color: 'text-[#FAB710]', bgColor: 'bg-[#FAB710]/20', icon: Star };
    }
    return { name: 'Free', color: 'text-white', bgColor: 'bg-white/20', icon: Heart };
  };

  const tierInfo = getTierInfo();
  const cleanDate = user?.clean_date || user?.sobriety_date;
  const TierIcon = tierInfo.icon;

  return (
    <div className="min-h-screen bg-gray-100 pb-24" data-testid="profile-page">
      <div className="lg:max-w-3xl lg:mx-auto lg:px-6 lg:pt-8">
      {/* Gradient Header */}
      <div className="relative bg-gradient-to-br from-[#1C3A7A] via-[#0d1f3c] to-[#061022] pt-12 pb-24 px-4 rounded-b-[40px] lg:rounded-[40px] overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-12 -right-12 w-36 h-36 rounded-full bg-white/[0.08]" />
        <div className="absolute bottom-5 -left-8 w-24 h-24 rounded-full bg-white/[0.05]" />
        
        {/* Settings Button */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <button 
            onClick={() => navigate("/settings")} 
            className="p-2 hover:bg-white/10 rounded-full transition"
            data-testid="settings-btn"
          >
            <Settings className="w-5 h-5 text-white" />
          </button>
          {user?.is_admin && (
            <button 
              onClick={() => navigate("/admin")} 
              className="p-2 hover:bg-white/10 rounded-full transition"
            >
              <Settings className="w-5 h-5 text-white/60" />
            </button>
          )}
        </div>

        {/* Profile Photo with Glowing Border */}
        <div className="flex flex-col items-center">
          <div className="relative">
            <div className="w-[124px] h-[124px] rounded-full p-1 bg-gradient-to-r from-[#FAB710] via-[#FFD966] to-[#FAB710]">
              <div className="w-full h-full rounded-full bg-white p-[3px]">
                {user?.photos?.[0] ? (
                  <img
                    src={user.photos[0]}
                    alt={user?.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full rounded-full bg-gradient-to-br from-[#1C3A7A] to-[#2d4a8a] flex items-center justify-center">
                    <Camera className="w-10 h-10 text-white/50" />
                  </div>
                )}
              </div>
            </div>
            {user?.is_verified && (
              <div className="absolute bottom-1 right-1 bg-white rounded-full p-0.5">
                <CheckCircle className="w-6 h-6 text-green-500 fill-green-500" />
              </div>
            )}
          </div>

          {/* Name */}
          <h1 className="text-2xl font-bold text-white mt-4 drop-shadow-md">
            {user?.name || 'User'}{user?.age ? `, ${user.age}` : ''}
          </h1>

          {/* Tier Badge */}
          <button
            onClick={() => navigate("/subscription")}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full mt-3 ${tierInfo.bgColor} transition hover:scale-105`}
            data-testid="tier-badge-btn"
          >
            <TierIcon className={`w-4 h-4 ${isElite || isPlus ? 'text-[#FAB710]' : 'text-white'}`} />
            <span className={`text-sm font-bold ${isElite || isPlus ? 'text-[#FAB710]' : 'text-white'}`}>
              {tierInfo.name}
            </span>
            {!isElite && <ChevronRight className={`w-3.5 h-3.5 ${isElite || isPlus ? 'text-[#FAB710]' : 'text-white'}`} />}
          </button>

          {/* Early Supporter Badge */}
          {(user?.is_early_supporter || user?.badges?.includes('early_supporter')) && (
            <div className="flex items-center gap-1.5 px-4 py-1.5 rounded-full mt-2 bg-purple-500/30">
              <Star className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-bold text-purple-400">Early Supporter</span>
            </div>
          )}

          {/* Day One Badge */}
          {user?.is_day_one && (
            <div className="flex items-center gap-1.5 px-4 py-1.5 rounded-full mt-2 bg-amber-500/30">
              <Crown className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-bold text-amber-400">Day One Member</span>
            </div>
          )}

          {/* Recovery Info Pills */}
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {user?.recovery_program && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20">
                <Shield className="w-3.5 h-3.5 text-white" />
                <span className="text-xs text-white font-semibold">
                  {Array.isArray(user.recovery_program) ? user.recovery_program[0] : user.recovery_program}
                </span>
              </div>
            )}
            {cleanDate && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20">
                <Calendar className="w-3.5 h-3.5 text-white" />
                <span className="text-xs text-white font-semibold">{formatSobriety(cleanDate)} clean</span>
              </div>
            )}
            {user?.location && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20">
                <MapPin className="w-3.5 h-3.5 text-white" />
                <span className="text-xs text-white font-semibold">{formatLocation(user.location)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Card - Floating */}
      <div className="px-4 -mt-12">
        <div className="bg-white rounded-2xl shadow-lg p-4 flex items-center justify-around">
          <div className="flex flex-col items-center">
            <div className="w-11 h-11 rounded-full bg-[#FAB710]/20 flex items-center justify-center mb-2">
              <Heart className="w-5 h-5 text-[#FAB710]" />
            </div>
            <span className="text-xl font-bold text-gray-900">{matchStats?.mutual_matches || 0}</span>
            <span className="text-xs text-gray-500 font-medium">Matches</span>
          </div>
          <div className="w-px h-16 bg-gray-200" />
          <div className="flex flex-col items-center">
            <div className="w-11 h-11 rounded-full bg-[#1C3A7A]/20 flex items-center justify-center mb-2">
              <MessageCircle className="w-5 h-5 text-[#1C3A7A]" />
            </div>
            <span className="text-xl font-bold text-gray-900">{matchStats?.conversations || 0}</span>
            <span className="text-xs text-gray-500 font-medium">Chats</span>
          </div>
          <div className="w-px h-16 bg-gray-200" />
          <div className="flex flex-col items-center">
            <div className="w-11 h-11 rounded-full bg-green-500/20 flex items-center justify-center mb-2">
              <Eye className="w-5 h-5 text-green-500" />
            </div>
            <span className="text-xl font-bold text-gray-900">{matchStats?.profile_views || 0}</span>
            <span className="text-xs text-gray-500 font-medium">Views</span>
          </div>
        </div>

        {/* Edit Profile Button */}
        <button
          onClick={() => navigate("/profile/edit")}
          className="w-full mt-4 bg-gradient-to-r from-[#1C3A7A] via-[#0d1f3c] to-[#061022] text-white py-3.5 rounded-full font-bold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
          data-testid="edit-profile-btn"
        >
          <Pencil className="w-4 h-4" />
          Edit Profile
        </button>
      </div>

      {/* Profile Completion Card */}
      {profileCompletion && profileCompletion.completion?.percentage < 100 && (
        <div className="px-4 mt-4">
          <button
            onClick={() => navigate("/profile/edit")}
            className="w-full bg-gradient-to-r from-[#FAB710]/10 to-amber-50 border border-[#FAB710]/30 rounded-2xl p-4 text-left"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-gray-900">Complete Your Profile</span>
              <span className="text-[#FAB710] font-bold">{profileCompletion.completion?.percentage || 0}%</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#FAB710] to-amber-400 rounded-full transition-all"
                style={{ width: `${profileCompletion.completion?.percentage || 0}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 mt-2">{profileCompletion.message || 'Add more details to get better matches!'}</p>
          </button>
        </div>
      )}

      {/* Premium Features - Only for Plus/Elite */}
      {(isPlus || isElite) && (
        <div className="px-4 mt-6">
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">Premium Features</h2>
          
          <MenuItem
            icon={Eye}
            title="Profile Insights"
            subtitle="See who viewed you & engagement stats"
            onClick={() => navigate("/insights")}
          />

          {isElite && (
            <MenuItem
              icon={Settings}
              title="Advanced Filters"
              subtitle="18+ filters for better matches"
              onClick={() => navigate("/advanced-filters")}
              badge="ELITE"
              badgeColor="bg-[#1C3A7A]"
            />
          )}
        </div>
      )}

      {/* Account Section */}
      <div className="px-4 mt-6">
        <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">Account</h2>
        
        <MenuItem
          icon={Gift}
          title="Gift Shop"
          subtitle="Send gifts & view your coins"
          onClick={() => navigate("/gift-shop")}
        />
        
        <MenuItem
          icon={Star}
          title="Subscription"
          subtitle={tierInfo.name}
          onClick={() => navigate("/subscription")}
        />

        <MenuItem
          icon={Bell}
          title="Notifications"
          subtitle="Manage push notifications"
          onClick={() => navigate("/settings")}
        />

        <MenuItem
          icon={Users}
          title="Referral Program"
          subtitle="Earn free Recovery+ days"
          onClick={() => navigate("/referral")}
        />

        <MenuItem
          icon={CheckCircle}
          title="Verification"
          subtitle={user?.is_verified ? 'Verified' : 'Get verified'}
          onClick={() => navigate("/verification")}
          badge={user?.is_verified ? '✓' : null}
          badgeColor="bg-green-500"
        />

        <MenuItem
          icon={Trophy}
          title="Leaderboard"
          subtitle="See top community members"
          onClick={() => navigate("/leaderboard")}
        />
      </div>

      {/* Safety & Support Section */}
      <div className="px-4 mt-6">
        <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">Safety & Support</h2>
        
        <MenuItem
          icon={Shield}
          title="Safety Tips"
          subtitle="Dating safety resources"
          onClick={() => navigate("/safety")}
        />

        <MenuItem
          icon={HelpCircle}
          title="Help & Support"
          subtitle="Get help or report issues"
          onClick={() => navigate("/support")}
        />
      </div>

      {/* Legal Section */}
      <div className="px-4 mt-6">
        <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">Legal</h2>
        
        <MenuItem
          icon={FileText}
          title="Terms of Service"
          onClick={() => navigate("/terms")}
        />

        <MenuItem
          icon={Lock}
          title="Privacy Policy"
          onClick={() => navigate("/privacy")}
        />
      </div>

      {/* Account Actions */}
      <div className="px-4 mt-6">
        <h3 className="text-sm font-semibold text-gray-500 mb-3 px-1">Account Actions</h3>
        <button
          onClick={() => navigate("/account/delete")}
          className="w-full flex items-center justify-between px-4 py-3.5 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition"
          data-testid="delete-account-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-red-600">Delete Account</p>
              <p className="text-xs text-red-400">Permanently delete your account</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-red-400" />
        </button>
      </div>

      {/* Logout Button */}
      <div className="px-4 mt-8">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-3.5 border-2 border-red-500 text-red-500 rounded-xl font-semibold hover:bg-red-50 transition"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </div>

      {/* Footer */}
      <div className="text-center mt-8 pb-4">
        <p className="text-xs text-gray-400">RecoveryMatch v1.0.0</p>
        <p className="text-xs text-gray-400 mt-1">© 2025 RecoveryMatch.com, LLC. All rights reserved.</p>
        
        <div className="mt-6 mb-4">
          <p className="text-sm text-[#FAB710] font-semibold">Made with ❤️ in Recovery!</p>
          <p className="text-xs text-gray-500 italic mt-1">"Dedicated to everyone brave enough to start again!"</p>
          <p className="text-xs text-gray-400 mt-2">Founded by Tracy Perrin</p>
          <p className="text-xs text-gray-300 mt-1">Built with Emergent AI</p>
        </div>
      </div>
      </div>

      <BottomNav />
    </div>
  );
};

// Menu Item Component
const MenuItem = ({ icon: Icon, title, subtitle, onClick, badge, badgeColor }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center bg-white p-3.5 rounded-2xl mb-2 shadow-sm hover:shadow-md transition-all text-left"
  >
    <div className="w-10 h-10 rounded-xl bg-[#1C3A7A]/10 flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#1C3A7A]" />
    </div>
    <div className="flex-1 ml-3">
      <p className="font-semibold text-gray-900 text-sm">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    {badge && (
      <span className={`text-[10px] font-bold text-white px-2 py-1 rounded-lg mr-2 ${badgeColor || 'bg-[#1C3A7A]'}`}>
        {badge}
      </span>
    )}
    <ChevronRight className="w-5 h-5 text-gray-400" />
  </button>
);

export default ProfilePage;
